import React from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, MessageSquare, TrendingUp, Zap, Plus, Facebook, Instagram, MessageCircle, Workflow } from 'lucide-react';
import { mockFlows, mockAnalytics } from '../data/mockData';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const navigate = useNavigate();

  const stats = [
    {
      title: 'Total Subscribers',
      value: mockAnalytics.totalSubscribers.toLocaleString(),
      change: '+12.5%',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Active Flows',
      value: mockAnalytics.activeFlows,
      change: '+3',
      icon: Workflow,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Messages Sent',
      value: mockAnalytics.messagesSent.toLocaleString(),
      change: '+8.2%',
      icon: MessageSquare,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Conversion Rate',
      value: `${mockAnalytics.conversionRate}%`,
      change: '+2.4%',
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  const platformIcons = {
    messenger: Facebook,
    instagram: Instagram,
    whatsapp: MessageCircle
  };

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back! Here's what's happening.</p>
          </div>
          <Button onClick={() => navigate('/flows')}>
            <Plus className="h-4 w-4 mr-2" />
            Create New Flow
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                      <p className="text-sm text-green-600 mt-1">{stat.change}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Flows */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Flows</CardTitle>
              <CardDescription>Your latest automation flows</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockFlows.map((flow) => {
                  const PlatformIcon = platformIcons[flow.platform];
                  return (
                    <div
                      key={flow.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/flows/${flow.id}`)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <PlatformIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{flow.name}</p>
                          <p className="text-sm text-gray-500">
                            {flow.subscribers.toLocaleString()} subscribers
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={flow.status === 'active' ? 'px-2 py-1 text-xs rounded-full bg-green-100 text-green-700' : 'px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700'}
                        >
                          {flow.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Platform Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Platform Distribution</CardTitle>
              <CardDescription>Subscribers by platform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Facebook className="h-5 w-5 text-gray-600" />
                      <span className="font-medium text-gray-900">Messenger</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      2,340 (51%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: '51%' }} />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Instagram className="h-5 w-5 text-gray-600" />
                      <span className="font-medium text-gray-900">Instagram</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      1,450 (32%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: '32%' }} />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MessageCircle className="h-5 w-5 text-gray-600" />
                      <span className="font-medium text-gray-900">WhatsApp</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      790 (17%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: '17%' }} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
